import dayjs from 'dayjs';

/**
 * Начален момент на работа за календар (scheduled_date + optional scheduled_time).
 */
export function parseJobScheduledStart(job) {
  if (!job) return null;
  if (job.scheduled_date) {
    const raw = String(job.scheduled_date);
    // Some backends return an ISO datetime with timezone (e.g. "...Z").
    // For scheduling we treat the stored time as a "floating local" value
    // (what the office typed), so we strip any explicit timezone offset.
    const rawNoTz = raw.replace(/(Z|[+-]\d{2}:\d{2})$/, '');
    let d = dayjs(rawNoTz);
    if (!d.isValid()) return null;
    const timeLooksDateOnly =
      !raw.includes('T') ||
      (d.hour() === 0 && d.minute() === 0 && d.second() === 0);
    const t = job.scheduled_time;
    if (t != null && String(t).trim() !== '' && timeLooksDateOnly) {
      const m = String(t)
        .trim()
        .match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
      if (m) {
        d = d.hour(parseInt(m[1], 10))
          .minute(parseInt(m[2], 10))
          .second(m[3] ? parseInt(m[3], 10) : 0)
          .millisecond(0);
      }
    }
    return d;
  }
  if (job.date) {
    const d = dayjs(job.date);
    return d.isValid() ? d : null;
  }
  return null;
}

export function jobMatchesDay(job, day) {
  const start = parseJobScheduledStart(job);
  if (start && start.isValid()) return start.isSame(day, 'day');
  if (job.created_at) return dayjs(job.created_at).isSame(day, 'day');
  return false;
}

/**
 * @returns {{ unscheduled: object[], byHour: Record<number, object[]> }}
 */
export function bucketJobsForDay(jobs, day) {
  const unscheduled = [];
  const byHour = {};
  for (let h = 0; h < 24; h += 1) byHour[h] = [];

  for (const job of jobs) {
    if (!jobMatchesDay(job, day)) continue;
    const start = parseJobScheduledStart(job);
    if (!start || !start.isValid() || !start.isSame(day, 'day')) {
      unscheduled.push(job);
      continue;
    }
    const hour = Math.min(23, Math.max(0, start.hour()));
    byHour[hour].push(job);
  }

  for (let h = 0; h < 24; h += 1) {
    byHour[h].sort((a, b) => {
      const da = parseJobScheduledStart(a);
      const db = parseJobScheduledStart(b);
      if (!da || !db) return 0;
      return da.diff(db);
    });
  }

  return { unscheduled, byHour };
}

/** Брой задачи по ден за даден месец (ключ YYYY-MM-DD). */
export function countJobsByDayInMonth(jobs, monthDayjs) {
  const map = {};
  const start = monthDayjs.startOf('month');
  const end = monthDayjs.endOf('month');
  for (const job of jobs) {
    const d = parseJobScheduledStart(job) || (job.created_at ? dayjs(job.created_at) : null);
    if (!d || !d.isValid()) continue;
    if (d.isBefore(start, 'day') || d.isAfter(end, 'day')) continue;
    const key = d.format('YYYY-MM-DD');
    map[key] = (map[key] || 0) + 1;
  }
  return map;
}

export function formatJobTime(job) {
  const d = parseJobScheduledStart(job);
  if (!d || !d.isValid()) return '';
  return d.format('HH:mm');
}

/** When the job was added to WorkWiseSoft (created_at). */
export function formatJobEnteredAt(job) {
  if (!job?.created_at) return null;
  const d = dayjs(job.created_at);
  return d.isValid() ? d.format('D MMM YYYY, HH:mm') : null;
}

/** When the work is planned (scheduled_date + optional scheduled_time). */
export function formatJobScheduledFor(job) {
  const d = parseJobScheduledStart(job);
  return d?.isValid() ? d.format('D MMM YYYY, HH:mm') : null;
}

/** True when the job should appear at a specific time (not all-day). */
export function jobHasScheduledTime(job) {
  if (!job) return false;
  const t = job.scheduled_time;
  if (t != null && String(t).trim() !== '') return true;
  const raw = String(job.scheduled_date || '');
  if (!raw.includes('T')) return false;
  const d = parseJobScheduledStart(job);
  if (!d?.isValid()) return false;
  return !(d.hour() === 0 && d.minute() === 0 && d.second() === 0);
}

/**
 * Map collections to FullCalendar events (timed + all-day).
 * Falls back to created_at for jobs without scheduled_date.
 */
export function jobsToCalendarEvents(jobs) {
  const list = Array.isArray(jobs) ? jobs : [];
  return list
    .map((job) => {
      let start = parseJobScheduledStart(job);
      if (!start?.isValid() && job.created_at) {
        start = dayjs(job.created_at);
      }
      if (!start?.isValid()) return null;

      const hasTime = jobHasScheduledTime(job);
      const title = `${job.driver ? `${job.driver}: ` : ''}${job.client || job.name || 'Unnamed Job'}`;

      return {
        id: String(job.id),
        title,
        start: hasTime ? start.format('YYYY-MM-DDTHH:mm:ss') : start.format('YYYY-MM-DD'),
        allDay: !hasTime,
        extendedProps: {
          address: job.address,
          phone: job.phone,
          email: job.email,
          waste_type: job.waste_type,
          notes: job.notes,
          price: job.price,
          currency: job.currency,
          status: job.status,
          is_problem: job.is_problem,
          scheduled_time: job.scheduled_time,
          scheduled_date: job.scheduled_date,
          created_at: job.created_at,
          driver_notes: job.driver_notes,
          files: job.files,
        },
      };
    })
    .filter(Boolean);
}

/** Google Maps directions to address (uses device location as start when the app allows). */
export function googleMapsDirectionsUrl(address) {
  const q = (address || '').trim();
  if (!q) return '';
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(q)}`;
}

/**
 * Jobs for a calendar day, ordered for a driver run: active first (by time), then unscheduled/tail, completed/cancelled last.
 */
export function sortTodaysStops(jobs, day) {
  const list = (jobs || []).filter((j) => jobMatchesDay(j, day));
  const statusRank = (s) => {
    const x = String(s || '').toLowerCase();
    if (x === 'completed' || x === 'cancelled') return 2;
    if (x === 'in_progress') return 0;
    return 1;
  };
  return [...list].sort((a, b) => {
    const ra = statusRank(a.status);
    const rb = statusRank(b.status);
    if (ra !== rb) return ra - rb;
    const da = parseJobScheduledStart(a);
    const db = parseJobScheduledStart(b);
    if (da && db && da.isValid() && db.isValid()) return da.valueOf() - db.valueOf();
    if (da?.isValid() && !db?.isValid()) return -1;
    if (!da?.isValid() && db?.isValid()) return 1;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  });
}
